import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Terminal } from "lucide-react";

const navLinks: Record<string, { label: string, href: string }[]> = {
  fr: [
    { label: "À propos", href: "#about" },
    { label: "Projets", href: "#projects" },
    { label: "Expériences", href: "#experience" },
    { label: "Formations", href: "#education" },
    { label: "Recherche", href: "#publications" },
    { label: "Contact", href: "#contact" },
  ],
  en: [
    { label: "About", href: "#about" },
    { label: "Projects", href: "#projects" },
    { label: "Experience", href: "#experience" },
    { label: "Education", href: "#education" },
    { label: "Research", href: "#publications" },
    { label: "Contact", href: "#contact" },
  ]
};

const BURGER_THRESHOLD = 0.65;

/*──────────────────────────────────────────────
  Shared desktop nav items
  (rendered both in the ghost and in the visible pill)
──────────────────────────────────────────────*/
function DesktopItems({
  language,
  activeSection,
  onNav,
  setLanguage,
  logoName,
  interactive = true,
}: {
  language: string;
  activeSection: string;
  onNav: (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>, href: string) => void;
  setLanguage?: (l: string) => void;
  logoName: string;
  interactive?: boolean;
}) {
  return (
    <>
      {/* Logo */}
      <a
        href="#"
        onClick={interactive ? (e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); } : undefined}
        className="flex items-center gap-2 px-3 py-1.5 rounded-full mr-2"
        style={{ background: "rgba(34,211,238,0.08)" }}
        tabIndex={interactive ? 0 : -1}
      >
        <Terminal size={14} color="#22d3ee" />
        <span
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: "12px",
            color: "#22d3ee",
            letterSpacing: "0.05em",
            whiteSpace: "nowrap",
          }}
        >
          {logoName}
        </span>
      </a>

      {/* Links */}
      <div className="flex items-center gap-1">
        {navLinks[language].map((link) => {
          const id = link.href.replace("#", "");
          const isActive = interactive && activeSection === id;
          return (
            <a
              key={link.href}
              href={link.href}
              onClick={interactive ? (e) => onNav(e, link.href) : undefined}
              className="relative px-4 py-1.5 rounded-full transition-colors"
              tabIndex={interactive ? 0 : -1}
              style={{
                fontFamily: "'Space Grotesk', sans-serif",
                fontSize: "13px",
                color: isActive ? "#22d3ee" : "rgba(255,255,255,0.6)",
                transition: "color 0.2s",
                whiteSpace: "nowrap",
              }}
            >
              {isActive && (
                <motion.span
                  layoutId="activeNav"
                  className="absolute inset-0 rounded-full"
                  style={{ background: "rgba(34,211,238,0.1)" }}
                  transition={{ type: "spring", stiffness: 380, damping: 30 }}
                />
              )}
              <span className="relative z-10">{link.label}</span>
            </a>
          );
        })}
      </div>

      {/* Action buttons */}
      <div className="flex items-center gap-2 ml-2">
        {setLanguage && (
          <button
            onClick={interactive ? () => setLanguage(language === "fr" ? "en" : "fr") : undefined}
            className="px-3 py-1.5 rounded-full"
            tabIndex={interactive ? 0 : -1}
            style={{
              background: "rgba(255,255,255,0.05)",
              border: "1px solid rgba(255,255,255,0.1)",
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: "11px",
              color: "#fff",
              cursor: "none",
            }}
          >
            {language === "fr" ? "EN" : "FR"}
          </button>
        )}
        <a
          href={`/dev?lang=${language}`}
          className="flex items-center gap-2 ml-2 px-4 py-1.5 rounded-full"
          tabIndex={interactive ? 0 : -1}
          style={{
            background: "linear-gradient(135deg, #22d3ee20, #a78bfa20)",
            border: "1px solid rgba(34,211,238,0.3)",
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: "11px",
            color: "#22d3ee",
            letterSpacing: "0.05em",
            whiteSpace: "nowrap",
          }}
        >
          &gt; open editor_
        </a>
      </div>
    </>
  );
}

/*──────────────────────────────────────────────
  Nav component
──────────────────────────────────────────────*/
export function Nav({ personalInfo, language = "fr", setLanguage }: { personalInfo?: any, language?: string, setLanguage?: (l: string) => void }) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("");
  const [navScale, setNavScale] = useState(1);

  const ghostRef = useRef<HTMLDivElement>(null);
  const useBurger = navScale < BURGER_THRESHOLD;

  // Measure ghost width vs viewport → compute zoom
  useEffect(() => {
    const check = () => {
      if (!ghostRef.current) return;
      const naturalWidth = ghostRef.current.offsetWidth;
      const available = window.innerWidth - 32;
      setNavScale(Math.min(1, available / naturalWidth));
    };
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, [language]);

  // Track scroll & active section
  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 60);
      const sections = ["about", "projects", "experience", "education", "publications", "contact"];
      for (const id of sections) {
        const el = document.getElementById(id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 120 && rect.bottom >= 120) {
            setActiveSection(id);
          }
        }
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close burger menu when growing back to desktop
  useEffect(() => {
    if (!useBurger) setMenuOpen(false);
  }, [useBurger]);

  const handleNav = (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>, href: string) => {
    e.preventDefault();
    const id = href.replace("#", "");
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setMenuOpen(false);
  };

  const logoName = personalInfo?.name ? personalInfo.name.split(" ")[0].toUpperCase() + ".DEV" : "SAMI.DEV";

  const sharedProps = { language, activeSection, onNav: handleNav, setLanguage, logoName };

  return (
    <>
      {/* ── Ghost: always-rendered hidden clone of full desktop nav, for measuring ── */}
      <div
        ref={ghostRef}
        aria-hidden="true"
        className="flex items-center gap-1 px-3 py-2"
        style={{
          position: "fixed",
          top: -9999,
          left: 0,
          visibility: "hidden",
          pointerEvents: "none",
          whiteSpace: "nowrap",
        }}
      >
        <DesktopItems {...sharedProps} interactive={false} />
      </div>

      {/* ── Visible nav ── */}
      <motion.nav
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="fixed top-4 left-0 right-0 z-[998] flex justify-end pr-4 pointer-events-none"
        style={{ cursor: "none" }}
      >
        <div
          className="flex items-center gap-1 px-3 py-2 rounded-full pointer-events-auto"
          style={{
            background: scrolled ? "rgba(8,8,12,0.92)" : "rgba(8,8,12,0.6)",
            backdropFilter: "blur(20px)",
            border: "1px solid rgba(34,211,238,0.15)",
            boxShadow: scrolled ? "0 0 30px rgba(34,211,238,0.08)" : "none",
            transition: "background 0.3s, box-shadow 0.3s",
            whiteSpace: "nowrap",
            // Zoom shrinks the pill layout, so centering works naturally
            zoom: useBurger ? 1 : navScale,
          }}
        >
          {!useBurger ? (
            // Desktop: full items, zoomed to fit
            <DesktopItems {...sharedProps} interactive={true} />
          ) : (
            // Burger mode
            <>
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                className="flex items-center gap-2 px-3 py-1.5 rounded-full mr-2"
                style={{ background: "rgba(34,211,238,0.08)" }}
              >
                <Terminal size={14} color="#22d3ee" />
                <span style={{
                  fontFamily: "'JetBrains Mono', monospace", fontSize: "12px",
                  color: "#22d3ee", letterSpacing: "0.05em", whiteSpace: "nowrap",
                }}>
                  {logoName}
                </span>
              </a>
              <button
                className="flex flex-col justify-center items-center w-8 h-8 gap-1.5 ml-1"
                onClick={() => setMenuOpen(!menuOpen)}
              >
                <span className="block w-5 h-px transition-all"
                  style={{ background: "#22d3ee", transform: menuOpen ? "rotate(45deg) translateY(4px)" : "none" }} />
                <span className="block w-5 h-px transition-all"
                  style={{ background: "#22d3ee", opacity: menuOpen ? 0 : 1 }} />
                <span className="block w-5 h-px transition-all"
                  style={{ background: "#22d3ee", transform: menuOpen ? "rotate(-45deg) translateY(-4px)" : "none" }} />
              </button>
            </>
          )}
        </div>
      </motion.nav>

      {/* Mobile dropdown */}
      <AnimatePresence>
        {menuOpen && useBurger && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed top-20 left-4 right-4 z-[997] rounded-2xl p-6 flex flex-col gap-3"
            style={{
              background: "rgba(8,8,12,0.97)",
              backdropFilter: "blur(20px)",
              border: "1px solid rgba(34,211,238,0.15)",
            }}
          >
            {navLinks[language].map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleNav(e, link.href)}
                className="py-3 border-b"
                style={{
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontSize: "18px",
                  color: "rgba(255,255,255,0.8)",
                  borderColor: "rgba(255,255,255,0.05)",
                }}
              >
                {link.label}
              </a>
            ))}
            {setLanguage && (
              <button
                onClick={() => setLanguage(language === "fr" ? "en" : "fr")}
                className="py-3 border-b text-left"
                style={{
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontSize: "18px",
                  color: "rgba(255,255,255,0.8)",
                  borderColor: "rgba(255,255,255,0.05)",
                }}
              >
                {language === "fr" ? "Switch to English" : "Passer en Français"}
              </button>
            )}
            <a
              href={`/dev?lang=${language}`}
              className="mt-2 py-3 text-center rounded-xl"
              style={{
                background: "linear-gradient(135deg, #22d3ee20, #a78bfa20)",
                border: "1px solid rgba(34,211,238,0.3)",
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: "13px",
                color: "#22d3ee",
              }}
            >
              &gt; open editor_
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
